import { Component, computed, inject, signal } from '@angular/core';
import type { AutocompleteOption } from '@ntv360/component-pantry';
import { Autocomplete } from '@ntv360/component-pantry';
import {
    FilterDropdownComponent,
    FilterOption,
} from '@shared/components/filter-dropdown/filter-dropdown.component';
import { SearchbarComponent } from '@shared/components/searchbar/searchbar.component';
import {
    MapControlsService,
    TerritoryMapService,
} from '../../../../shared/components/territory-map/services';
import { FAKE_SEARCH_DATA, LOCATOR_DATA } from '../../constants/locator.constants';
import { TerritoryListComponent } from '../territory-list/territory-list.component';

@Component({
    selector: 'app-locator-sidebar',
    imports: [SearchbarComponent, FilterDropdownComponent, TerritoryListComponent, Autocomplete],
    templateUrl: './locator-sidebar.component.html',
    styleUrl: './locator-sidebar.component.scss',
})
export class LocatorSidebarComponent {
    protected mapControlsService = inject(MapControlsService);
    protected territoryMapService = inject(TerritoryMapService);

    /**
     * Filter dropdown options
     */
    public filterOptions: FilterOption[] = [
        { label: 'Search by State', icon: 'map-folded' },
        { label: 'Search by Dealer', icon: 'dealers' },
        { label: 'Search by Host', icon: 'location-pin' },
        { label: 'Search by Territory', icon: 'merge' },
        { label: 'Search by Licenses', icon: 'shield' },
        { label: 'Filter by Category', icon: 'list-view' },
        { label: 'Filter by Status', icon: 'filter' },
    ];

    /**
     * Current searchbar placeholder — updates when a filter option is selected
     */
    public searchPlaceholder = signal<string>('Search by State');

    /**
     * Currently selected filter label — drives the autocomplete options
     */
    public selectedFilter = signal<string>('Search by State');

    /**
     * Autocomplete options derived from the selected filter type
     */
    public searchOptions = computed<AutocompleteOption[]>(() => {
        switch (this.selectedFilter()) {
            case 'Search by State':
                return FAKE_SEARCH_DATA.states.map((s) => ({ label: s.name, value: s.code }));
            case 'Search by Dealer':
                return LOCATOR_DATA.flatMap((t) =>
                    t.dealers.map((d) => ({ label: d.dealerName, value: d.dealerId })),
                ).filter((d, i, arr) => arr.findIndex((x) => x.value === d.value) === i);
            case 'Search by Host':
                return LOCATOR_DATA.flatMap((t) =>
                    t.host.map((h) => ({ label: h.hostName, value: h.hostName })),
                ).filter((h, i, arr) => arr.findIndex((x) => x.value === h.value) === i);
            case 'Search by Territory':
                return LOCATOR_DATA.map((t) => ({
                    label: t.territoryName,
                    value: t.territoryName,
                }));
            case 'Search by Licenses':
                return FAKE_SEARCH_DATA.licenses.map((l) => ({
                    label: l.code,
                    value: l.id,
                }));
            case 'Filter by Category':
                return FAKE_SEARCH_DATA.categories.map((c) => ({ label: c.name, value: c.name }));
            case 'Filter by Status':
                return FAKE_SEARCH_DATA.status.map((s) => ({ label: s.label, value: s.label }));
            default:
                return [];
        }
    });

    /**
     * Called when filter dropdown changes — updates both placeholder and options
     */
    public onFilterSelected(label: string): void {
        this.searchPlaceholder.set(label);
        this.selectedFilter.set(label);
    }

    /**
     * Currently active tool/drawer
     */
    public activeTool = signal<string | null>(null);

    /**
     * Active drawing control (view, draw, delete, undo, redo)
     */
    public activeDrawControl = signal<string | null>('view');

    /**
     * Set or toggle the active tool
     */
    public selectTool(tool: string): void {
        this.activeTool.update((current) => (current === tool ? null : tool));
    }

    /**
     * Handle drawing control button clicks
     */
    public selectDrawControl(control: string): void {
        const nonToggleable = ['delete', 'undo', 'redo'];
        if (!nonToggleable.includes(control)) {
            this.activeDrawControl.set(control);
        }

        switch (control) {
            case 'view':
                this.mapControlsService.view();
                break;
            case 'draw':
                this.mapControlsService.draw();
                break;
            case 'delete':
                this.mapControlsService.delete();
                break;
            case 'undo':
                this.mapControlsService.undo();
                break;
            case 'redo':
                this.mapControlsService.redo();
                break;
        }
    }
}
